import { Chart } from 'chart.js/auto';
import { ForecastWidgetConfig, WidgetRenderContext, WidgetRenderer } from '../types';
import { ForecastBinaryService } from '../../../../services/ForecastBinaryService';
import TierAccessService from '../../../../services/TierAccessService';

export class WindWidget implements WidgetRenderer {
  public renderHtml(widget: ForecastWidgetConfig, _ctx: WidgetRenderContext): string {
    const title = widget.title || 'Wind Speed & Direction';
    const height = widget.options?.height || 180;
    const canvasId = `poi-wind-canvas-${widget.id}`;

    return `
      <div class="metric-card" style="background: rgba(255,255,255,0.03); border: 1px solid var(--border-light); border-radius: 8px; padding: 12px;">
        <h3 style="margin: 0 0 12px 0; color: #f8fafc; font-size: 0.95rem;">💨 ${title}</h3>
        <div style="height: ${height}px; position: relative;">
          <canvas id="${canvasId}"></canvas>
        </div>
      </div>
    `;
  }

  public init(widget: ForecastWidgetConfig, ctx: WidgetRenderContext): void {
    const canvasId = `poi-wind-canvas-${widget.id}`;
    const canvas = document.getElementById(canvasId) as HTMLCanvasElement;
    if (!canvas) return;

    const data = ctx.data;
    if (!data.timeLabels || data.timeLabels.length === 0) {
      const parent = canvas.parentElement;
      if (parent) {
        canvas.style.display = 'none';
        parent.innerHTML = TierAccessService.buildTrendChartDenialHtml({
          denial: data?.accessDenial || ForecastBinaryService.lastAccessDenial,
          requiredTier: 'pro',
          featureLabel: 'hourly wind speed & direction trend charts'
        });
      }
      return;
    }

    const windDirectionPlugin = {
      id: 'windDirection',
      afterDatasetsDraw: (chart: Chart) => {
        const chartCtx = chart.ctx;
        const datasetMeta = chart.getDatasetMeta(0);

        chartCtx.save();
        chartCtx.textAlign = 'center';
        chartCtx.textBaseline = 'middle';

        const getCompassDirection8 = (deg: number): string => {
          const val = Math.floor((deg / 45) + 0.5);
          const arr = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
          return arr[(val % 8)];
        };

        datasetMeta.data.forEach((element: any, index: number) => {
          const dir = data.windDir[index];
          if (dir === undefined) return;

          const x = element.x;
          const arrowY = element.y - 14;
          const textY = element.y - 4;

          chartCtx.save();
          chartCtx.translate(x, arrowY);
          // Standard meteorology: arrow points where wind is blowing TO. WindDir is where it's coming FROM.
          chartCtx.rotate((dir + 180) * Math.PI / 180);
          chartCtx.fillStyle = '#ffffff';
          chartCtx.font = '10px Arial';
          chartCtx.fillText('↑', 0, 0);
          chartCtx.restore();

          chartCtx.save();
          chartCtx.fillStyle = '#94a3b8';
          chartCtx.font = '8px Arial';
          const compassText = getCompassDirection8(dir);
          chartCtx.fillText(compassText, x, textY);
          chartCtx.restore();
        });

        chartCtx.restore();
      }
    };

    const windChart = new Chart(canvas, {
      type: 'bar',
      plugins: [windDirectionPlugin],
      data: {
        labels: data.timeLabels,
        datasets: [
          {
            label: 'Wind Speed',
            data: data.windSpeed,
            backgroundColor: '#14b8a6'
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: { ticks: { color: '#94a3b8' }, grid: { color: 'rgba(255,255,255,0.05)' } },
          y: {
            beginAtZero: true,
            title: { display: true, text: 'Speed', color: '#94a3b8' },
            ticks: { color: '#94a3b8' },
            grid: { color: 'rgba(255,255,255,0.05)' }
          }
        },
        plugins: {
          legend: { labels: { color: '#f8fafc' } }
        }
      }
    });

    ctx.charts.push(windChart);
  }
}
